import React from 'react';
import { TrashIcon, PlusIcon } from '@heroicons/react/20/solid';
import { TWButton } from '../../../components/ui';

const ReviewStep = ({ menuData, setMenuData, onConfirm, confirming }) => {
  const updateMenuField = (field, value) => {
    setMenuData((prev) => ({
      ...prev,
      menu: { ...prev.menu, [field]: value },
    }));
  };

  const updateCategory = (catIndex, field, value) => {
    setMenuData((prev) => {
      const categories = [...prev.categories];
      categories[catIndex] = { ...categories[catIndex], [field]: value };
      return { ...prev, categories };
    });
  };

  const updateProduct = (catIndex, prodIndex, field, value) => {
    setMenuData((prev) => {
      const categories = [...prev.categories];
      const products = [...categories[catIndex].products];
      products[prodIndex] = { ...products[prodIndex], [field]: value };
      categories[catIndex] = { ...categories[catIndex], products };
      return { ...prev, categories };
    });
  };

  const updateItem = (catIndex, prodIndex, itemIndex, field, value) => {
    setMenuData((prev) => {
      const categories = [...prev.categories];
      const products = [...categories[catIndex].products];
      const items = [...products[prodIndex].items];
      items[itemIndex] = {
        ...items[itemIndex],
        [field]: field === 'price' ? parseFloat(value) || 0 : value,
      };
      products[prodIndex] = { ...products[prodIndex], items };
      categories[catIndex] = { ...categories[catIndex], products };
      return { ...prev, categories };
    });
  };

  const removeCategory = (catIndex) => {
    setMenuData((prev) => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== catIndex),
    }));
  };

  const removeProduct = (catIndex, prodIndex) => {
    setMenuData((prev) => {
      const categories = [...prev.categories];
      categories[catIndex] = {
        ...categories[catIndex],
        products: categories[catIndex].products.filter((_, i) => i !== prodIndex),
      };
      return { ...prev, categories };
    });
  };

  const addItem = (catIndex, prodIndex) => {
    setMenuData((prev) => {
      const categories = [...prev.categories];
      const products = [...categories[catIndex].products];
      const items = [...(products[prodIndex].items || []), { name: 'Regular', price: 0 }];
      products[prodIndex] = { ...products[prodIndex], items };
      categories[catIndex] = { ...categories[catIndex], products };
      return { ...prev, categories };
    });
  };

  const removeItem = (catIndex, prodIndex, itemIndex) => {
    setMenuData((prev) => {
      const categories = [...prev.categories];
      const products = [...categories[catIndex].products];
      const items = (products[prodIndex].items || []).filter((_, i) => i !== itemIndex);
      products[prodIndex] = { ...products[prodIndex], items };
      categories[catIndex] = { ...categories[catIndex], products };
      return { ...prev, categories };
    });
  };

  if (!menuData?.categories?.length) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        No categories found in parsed menu. Go back and try another file.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <label className="block text-xs font-medium uppercase text-slate-500">Menu name</label>
        <input
          type="text"
          value={menuData.menu?.name || ''}
          onChange={(e) => updateMenuField('name', e.target.value)}
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
        />
        <label className="mt-3 block text-xs font-medium uppercase text-slate-500">
          Description
        </label>
        <input
          type="text"
          value={menuData.menu?.description || ''}
          onChange={(e) => updateMenuField('description', e.target.value)}
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
        />
      </div>

      {menuData.categories.map((category, catIndex) => (
        <div
          key={catIndex}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium uppercase text-slate-500">
                Category
              </label>
              <input
                type="text"
                value={category.name || ''}
                onChange={(e) => updateCategory(catIndex, 'name', e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold dark:border-slate-600 dark:bg-slate-700 dark:text-white"
              />
            </div>
            <button
              type="button"
              onClick={() => removeCategory(catIndex)}
              className="mt-6 rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
              title="Remove category"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 space-y-3 pl-2 border-l-2 border-slate-100 dark:border-slate-600">
            {(category.products || []).map((product, prodIndex) => (
              <div
                key={prodIndex}
                className="rounded-lg border border-slate-100 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-700/40"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={product.name || ''}
                      onChange={(e) =>
                        updateProduct(catIndex, prodIndex, 'name', e.target.value)
                      }
                      placeholder="Product name"
                      className="block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                    />
                    <input
                      type="text"
                      value={product.description || ''}
                      onChange={(e) =>
                        updateProduct(catIndex, prodIndex, 'description', e.target.value)
                      }
                      placeholder="Description (optional)"
                      className="block w-full rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                    />
                    <div className="space-y-1">
                      {(product.items || []).map((item, itemIndex) => (
                        <div key={itemIndex} className="flex gap-2">
                          <input
                            type="text"
                            value={item.name || ''}
                            onChange={(e) =>
                              updateItem(catIndex, prodIndex, itemIndex, 'name', e.target.value)
                            }
                            placeholder="Size / variant"
                            className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                          />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.price ?? 0}
                            onChange={(e) =>
                              updateItem(catIndex, prodIndex, itemIndex, 'price', e.target.value)
                            }
                            className="w-24 rounded-md border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={() => removeItem(catIndex, prodIndex, itemIndex)}
                            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                            title="Remove price variant"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addItem(catIndex, prodIndex)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                      >
                        <PlusIcon className="h-3 w-3" /> Add price variant
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeProduct(catIndex, prodIndex)}
                    className="rounded p-1 text-slate-400 hover:text-rose-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
        <TWButton variant="primary" loading={confirming} onClick={onConfirm}>
          Confirm &amp; Import to System
        </TWButton>
      </div>
    </div>
  );
};

export default ReviewStep;
